import {
  createSearchParams,
  useNavigate,
  useSearchParams,
} from 'react-router-dom';

import env from '../../configs/env';
import { FileBrowserStorageObjectFragment } from '../../graphql/generated';
import { navigateToSearchPage } from '../../pages/search-results-page/search-results-page';

export const DEFAULT_PAGE_SIZE = env.app.defaults?.paginationLimit || 20;

type PaginationConfig = {
  offset?: number;
  limit?: number;
};

type UseSearchPaginationProps = {
  count: number;
  pageSize?: number;
  results: FileBrowserStorageObjectFragment[];
};

export type UseSearchPaginationBag = {
  pageSize: number;
  pageCount: number;
  page: FileBrowserStorageObjectFragment[];
  totalItemCount: number;
  currentPageNumber: number;
  handlePageChange: (pageNumber: number) => void;
};

/**
 *
 * @param results
 * @param count
 * @param pageSize
 */
export const useSearchPagination = ({
  results,
  count,
  pageSize = DEFAULT_PAGE_SIZE,
}: UseSearchPaginationProps): UseSearchPaginationBag => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const pageCount = Math.ceil(count / pageSize);
  const currentPageNumber = useGetCurrentPageNumber();

  const handlePageChange = (pageNumber: number) => {
    const newPath = navigateToSearchPage(
      createSearchParams({
        q: searchParams.get('q') || '',
        page: pageNumber.toString(),
        treaty: searchParams.getAll('treaty'),
      })
    );
    navigate(newPath);
  };

  return {
    pageSize,
    pageCount,
    page: results,
    handlePageChange,
    currentPageNumber,
    totalItemCount: count,
  };
};

export const useGetCurrentPageNumber = (): number => {
  const [searchParams] = useSearchParams();
  const currentPageNumberString = searchParams.get('page');
  const currentPageNumber = currentPageNumberString
    ? parseInt(currentPageNumberString, 10)
    : 1;

  return currentPageNumber;
};

export const usePaginationConfig = (
  pageSize: number = DEFAULT_PAGE_SIZE
): PaginationConfig => {
  const currentPageNumber = useGetCurrentPageNumber();

  return {
    offset: (currentPageNumber - 1) * pageSize || 0,
    limit: pageSize,
  };
};
